import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { X, Plus, Trash2, Edit2, Table2, GitBranch, Maximize2, Lock, RotateCcw, AlignJustify, Layers, Link2, Search } from "lucide-react";
import { CATEGORIES, EDGE_TYPES, DEFAULT_NODES, DEFAULT_EDGES } from "./relationMap.config";
import { scanNav, countUnregistered, ROLE_COLORS } from "./navScanner";

const NW = 130;
const NH = 36;
const LS_NODES   = "tg_relmap_nodes_v2";
const LS_EDGES   = "tg_relmap_edges_v2";
const LS_SANDBOX = "tg_sandbox_v1";

// ─── Persistence helpers ──────────────────────────────────────────────────────

function loadLayout(defaultNodes, defaultEdges) {
    try {
        const posMap    = JSON.parse(localStorage.getItem(LS_NODES)  || "{}");
        const rawEdges  = localStorage.getItem(LS_EDGES);
        const nodes = defaultNodes.map(n => ({ ...n, ...(posMap[n.id] || {}) }));
        const edges = rawEdges ? JSON.parse(rawEdges) : defaultEdges.map(e => ({ ...e }));
        return { nodes, edges };
    } catch {
        return { nodes: defaultNodes.map(n => ({ ...n })), edges: defaultEdges.map(e => ({ ...e })) };
    }
}

function savePositions(nodes) {
    const m = {};
    nodes.forEach(n => { m[n.id] = { x: n.x, y: n.y }; });
    localStorage.setItem(LS_NODES, JSON.stringify(m));
}
function saveEdges(edges) { localStorage.setItem(LS_EDGES, JSON.stringify(edges)); }

function loadSandbox() {
    try {
        const raw = localStorage.getItem(LS_SANDBOX);
        return raw ? JSON.parse(raw) : { nodes: [], edges: [] };
    } catch { return { nodes: [], edges: [] }; }
}
function saveSandbox(data) { localStorage.setItem(LS_SANDBOX, JSON.stringify(data)); }

// ─── Auto-align ───────────────────────────────────────────────────────────────

function autoAlign(nodes) {
    const COL_ORDER = ["external", "transaction", "orchestration", "fulfillment", "gate", "infra"];
    const colY = {};
    COL_ORDER.forEach(c => { colY[c] = 60; });
    const COL_X = 20, COL_GAP = 182, ROW_GAP = 74;

    const roleNodes = nodes.filter(n => n.category === "role");
    const sysNodes  = nodes.filter(n => n.category !== "role");

    const aligned = sysNodes.map(n => {
        const ci = Math.max(0, COL_ORDER.indexOf(n.category));
        const x  = COL_X + ci * COL_GAP;
        const y  = colY[n.category] ?? 60;
        colY[n.category] = (colY[n.category] ?? 60) + ROW_GAP;
        return { ...n, x, y };
    });

    const gap = roleNodes.length > 1 ? Math.min(163, 980 / (roleNodes.length - 1)) : 0;
    const roleAligned = roleNodes.map((n, i) => ({ ...n, x: 20 + i * gap, y: 670 }));
    return [...aligned, ...roleAligned];
}

// ─── SVG curve helpers ────────────────────────────────────────────────────────

function nc(n) { return { cx: n.x + NW / 2, cy: n.y + NH / 2 }; }

function curve(a, b, pull = 30) {
    const { cx: x1, cy: y1 } = nc(a), { cx: x2, cy: y2 } = nc(b);
    const mx = (x1+x2)/2, my = (y1+y2)/2, dx = x2-x1, dy = y2-y1;
    const l = Math.sqrt(dx*dx+dy*dy) || 1;
    return `M ${x1} ${y1} Q ${mx-(dy/l)*pull} ${my+(dx/l)*pull} ${x2} ${y2}`;
}

function mid(a, b, pull = 30) {
    const { cx: x1, cy: y1 } = nc(a), { cx: x2, cy: y2 } = nc(b);
    const mx = (x1+x2)/2, my = (y1+y2)/2, dx = x2-x1, dy = y2-y1;
    const l = Math.sqrt(dx*dx+dy*dy) || 1;
    const cpx = mx-(dy/l)*pull, cpy = my+(dx/l)*pull;
    return { x: x1/4+cpx/2+x2/4, y: y1/4+cpy/2+y2/4 };
}

// ─── Diagram (live, locked) ───────────────────────────────────────────────────

function Diagram({ nodes, edges, setNodes, setEdges, onAlign, onResetLayout, savedFlash }) {
    const svgRef = useRef(null);
    const [zoom, setZoom]     = useState(0.78);
    const [pan, setPan]       = useState({ x: 14, y: 12 });
    const [panning, setPanning] = useState(null);
    const [dragging, setDragging]     = useState(null);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [hoveredEdge, setHoveredEdge] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [editLabel, setEditLabel]       = useState("");
    const [filters, setFilters] = useState(
        Object.fromEntries(Object.keys(EDGE_TYPES).map(k => [k, true]))
    );

    const byId = {};
    nodes.forEach(n => (byId[n.id] = n));

    const toSvg = useCallback((cx, cy) => {
        const r = svgRef.current.getBoundingClientRect();
        return { x: (cx - r.left - pan.x) / zoom, y: (cy - r.top - pan.y) / zoom };
    }, [pan, zoom]);

    const onWheel = useCallback((e) => {
        e.preventDefault();
        const f = e.deltaY < 0 ? 1.13 : 0.89;
        const r = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - r.left, my = e.clientY - r.top;
        setZoom(z => {
            const nz = Math.min(4, Math.max(0.25, z * f));
            setPan(p => ({ x: mx-(mx-p.x)*(nz/z), y: my-(my-p.y)*(nz/z) }));
            return nz;
        });
    }, []);

    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [onWheel]);

    const onSvgMouseDown = useCallback((e) => {
        if (!dragging) { e.preventDefault(); setPanning({ sx: e.clientX - pan.x, sy: e.clientY - pan.y }); }
    }, [pan, dragging]);

    const onNodeMouseDown = useCallback((e, id) => {
        e.stopPropagation(); e.preventDefault();
        const { x, y } = toSvg(e.clientX, e.clientY);
        const n = byId[id];
        setDragging({ id, ox: x - n.x, oy: y - n.y });
        setSelectedEdge(null); setSelectedNode(null);
    }, [byId, toSvg]);

    const onMouseMove = useCallback((e) => {
        if (dragging) {
            const { x, y } = toSvg(e.clientX, e.clientY);
            setNodes(prev => prev.map(n =>
                n.id === dragging.id ? { ...n, x: Math.max(0, x-dragging.ox), y: Math.max(0, y-dragging.oy) } : n
            ));
        } else if (panning) {
            setPan({ x: e.clientX - panning.sx, y: e.clientY - panning.sy });
        }
    }, [dragging, panning, toSvg, setNodes]);

    const onMouseUp = useCallback(() => { setDragging(null); setPanning(null); }, []);

    const onNodeClick = useCallback((e, id) => {
        e.stopPropagation();
        if (dragging) return;
        setSelectedNode(id); setEditLabel(byId[id]?.label || ""); setSelectedEdge(null);
    }, [dragging, byId]);

    const saveLabel = () => {
        if (editLabel.trim()) setNodes(prev => prev.map(n => n.id === selectedNode ? { ...n, label: editLabel } : n));
        setSelectedNode(null);
    };
    const deleteEdge = (id) => { setEdges(prev => prev.filter(e => e.id !== id)); setSelectedEdge(null); };

    const resetView = () => { setZoom(0.78); setPan({ x: 14, y: 12 }); };

    const visibleEdges = edges.filter(e => filters[e.type] !== false);
    const connectedIds = hoveredNode
        ? new Set(visibleEdges.filter(e => e.from === hoveredNode || e.to === hoveredNode).map(e => e.id))
        : null;
    const selEdge = edges.find(e => e.id === selectedEdge);
    const roleY = (() => { const ry = nodes.filter(n => n.category === "role").map(n => n.y); return ry.length ? Math.min(...ry) : null; })();

    return (
        <div className="relative flex-1 overflow-hidden bg-[#07101e] rounded-xl select-none">

            {/* Edge type filters */}
            <div className="absolute top-3 left-3 z-20 flex flex-wrap gap-1.5">
                {Object.entries(EDGE_TYPES).map(([key, et]) => {
                    const on = filters[key] !== false;
                    return (
                        <button key={key} onClick={() => setFilters(f => ({ ...f, [key]: !f[key] }))}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-bold border transition-all"
                            style={{ color: on ? et.color : "#475569", borderColor: on ? et.color+"55" : "#1e293b", background: on ? et.color+"18" : "transparent" }}>
                            <span className="w-4 h-px inline-block flex-shrink-0"
                                style={{ background: on ? et.color : "#334155", borderTop: et.dash ? `1px dashed ${on ? et.color : "#334155"}` : "none" }} />
                            {et.label}
                        </button>
                    );
                })}
            </div>

            {/* Zoom + utility controls */}
            <div className="absolute top-3 right-3 z-20 flex flex-col items-center gap-1">
                <button onClick={() => setZoom(z => Math.min(4, z * 1.2))}
                    className="w-7 h-7 bg-slate-800/90 hover:bg-slate-700 text-slate-300 rounded-lg flex items-center justify-center text-sm font-bold border border-slate-700">+</button>
                <span className="text-[9px] text-slate-500 font-mono py-0.5">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(z => Math.max(0.25, z / 1.2))}
                    className="w-7 h-7 bg-slate-800/90 hover:bg-slate-700 text-slate-300 rounded-lg flex items-center justify-center text-sm font-bold border border-slate-700">−</button>
                <button onClick={resetView} title="Fit view"
                    className="w-7 h-7 bg-slate-800/90 hover:bg-slate-700 text-slate-400 rounded-lg flex items-center justify-center border border-slate-700 mt-1">
                    <Maximize2 size={11} />
                </button>
                <button onClick={onAlign} title="Auto-align nodes by category"
                    className="w-7 h-7 bg-slate-800/90 hover:bg-indigo-900/80 text-slate-400 hover:text-indigo-300 rounded-lg flex items-center justify-center border border-slate-700 transition-colors">
                    <AlignJustify size={11} />
                </button>
                <button onClick={onResetLayout} title="Reset to default positions"
                    className="w-7 h-7 bg-slate-800/90 hover:bg-red-900/60 text-slate-500 hover:text-red-400 rounded-lg flex items-center justify-center border border-slate-700 transition-colors">
                    <RotateCcw size={10} />
                </button>
                {savedFlash && <span className="text-[8px] text-emerald-400 font-bold font-mono animate-pulse mt-0.5">saved</span>}
            </div>

            {/* SVG */}
            <svg ref={svgRef} className="w-full h-full"
                onMouseDown={onSvgMouseDown} onMouseMove={onMouseMove}
                onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
                onClick={() => { setSelectedEdge(null); setSelectedNode(null); }}
                style={{ cursor: dragging ? "grabbing" : panning ? "grabbing" : "default" }}>

                <defs>
                    {Object.entries(EDGE_TYPES).map(([key, et]) => (
                        <marker key={key} id={`arr-${key}`} markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
                            <path d="M0,0.5 L0,5.5 L7,3 z" fill={et.color} />
                        </marker>
                    ))}
                </defs>

                <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>

                    {roleY != null && (
                        <>
                            <line x1={0} y1={roleY-18} x2={1100} y2={roleY-18} stroke="#1e3a5f" strokeWidth={1} strokeDasharray="6 4" />
                            <text x={8} y={roleY-24} fontSize={8} fontWeight="800" fill="#334d6e" style={{ letterSpacing: "0.14em" }}>USER ROLES</text>
                        </>
                    )}

                    {visibleEdges.map((edge, idx) => {
                        const from = byId[edge.from], to = byId[edge.to];
                        if (!from || !to) return null;
                        const et    = EDGE_TYPES[edge.type] || EDGE_TYPES.request;
                        const isHov = hoveredEdge === edge.id, isSel = selectedEdge === edge.id;
                        const pull  = 28 + (idx % 6) * 9;
                        const dim   = connectedIds !== null && !connectedIds.has(edge.id);
                        const op    = isSel ? 1 : isHov ? 0.95 : dim ? 0.04 : 0.55;
                        const sw    = isSel ? 2.5 : isHov ? 2 : 1.4;
                        const d     = curve(from, to, pull);
                        const m     = mid(from, to, pull);
                        return (
                            <g key={edge.id} style={{ transition: "opacity 0.12s" }}>
                                <path d={d} fill="none" stroke="transparent" strokeWidth={12} style={{ cursor: "pointer" }}
                                    onMouseEnter={() => setHoveredEdge(edge.id)} onMouseLeave={() => setHoveredEdge(null)}
                                    onClick={e => { e.stopPropagation(); setSelectedEdge(edge.id); setSelectedNode(null); }} />
                                <path d={d} fill="none" stroke={et.color} strokeWidth={sw} strokeDasharray={et.dash}
                                    markerEnd={`url(#arr-${edge.type})`} opacity={op}
                                    onMouseEnter={() => setHoveredEdge(edge.id)} onMouseLeave={() => setHoveredEdge(null)}
                                    onClick={e => { e.stopPropagation(); setSelectedEdge(edge.id); setSelectedNode(null); }}
                                    style={{ cursor: "pointer", transition: "opacity 0.12s, stroke-width 0.1s" }} />
                                {(isHov || isSel) && (
                                    <text x={m.x} y={m.y-6} textAnchor="middle" fill={et.color} fontSize={9} fontWeight="700"
                                        style={{ pointerEvents: "none", filter: "drop-shadow(0 1px 2px #07101e)" }}>
                                        {edge.label}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {nodes.map(node => {
                        const cat   = CATEGORIES[node.category] || CATEGORIES.infra;
                        const isRole = node.category === "role";
                        const isSel  = selectedNode === node.id;
                        const isHovN = hoveredNode === node.id;
                        const dimNode = connectedIds !== null && !isHovN &&
                            !visibleEdges.some(e => (e.from === node.id || e.to === node.id) && connectedIds.has(e.id));
                        return (
                            <g key={node.id} transform={`translate(${node.x},${node.y})`}
                                style={{ cursor: "grab", transition: "opacity 0.12s" }}
                                opacity={dimNode ? 0.18 : 1}
                                onMouseDown={e => onNodeMouseDown(e, node.id)}
                                onClick={e => onNodeClick(e, node.id)}
                                onMouseEnter={() => setHoveredNode(node.id)}
                                onMouseLeave={() => setHoveredNode(null)}>
                                {isRole ? (
                                    <>
                                        <rect width={NW} height={NH} rx={NH/2}
                                            fill={isSel||isHovN ? cat.color+"28" : cat.color+"14"}
                                            stroke={cat.color} strokeWidth={isSel ? 2.5 : 1.5} strokeDasharray="4 2.5"
                                            filter={isSel ? `drop-shadow(0 0 7px ${cat.color}90)` : undefined} />
                                        <ellipse cx={16} cy={NH/2-5} rx={4} ry={4} fill={cat.color} opacity={0.85} />
                                        <path d={`M8,${NH/2+8} Q16,${NH/2+2} 24,${NH/2+8}`} fill={cat.color} opacity={0.85} />
                                        <text x={NW/2+5} y={NH/2+1} textAnchor="middle" dominantBaseline="middle"
                                            fontSize={10} fontWeight="800" fill={cat.color}
                                            style={{ pointerEvents: "none", userSelect: "none" }}>{node.label}</text>
                                    </>
                                ) : (
                                    <>
                                        <rect width={NW} height={NH} rx={8}
                                            fill={isSel||isHovN ? cat.color+"28" : cat.color+"16"}
                                            stroke={cat.color} strokeWidth={isSel ? 2.5 : isHovN ? 2 : 1.5}
                                            filter={isSel ? `drop-shadow(0 0 8px ${cat.color}90)` : undefined} />
                                        <text x={NW/2} y={NH/2+1} textAnchor="middle" dominantBaseline="middle"
                                            fontSize={10} fontWeight="700" fill={cat.color}
                                            style={{ pointerEvents: "none", userSelect: "none" }}>{node.label}</text>
                                    </>
                                )}
                            </g>
                        );
                    })}
                </g>
            </svg>

            {selEdge && (() => {
                const et = EDGE_TYPES[selEdge.type] || EDGE_TYPES.request;
                return (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/95 border border-slate-700 rounded-2xl px-5 py-3 flex items-center gap-4 shadow-2xl backdrop-blur-sm">
                        <div>
                            <p className="text-[9px] text-slate-500 uppercase tracking-widest">Edge</p>
                            <p className="text-sm font-bold text-white mt-0.5">{selEdge.label}</p>
                            <p className="text-[10px] mt-0.5 font-mono" style={{ color: et.color }}>{selEdge.from} → {selEdge.to} · {et.label}</p>
                        </div>
                        <button onClick={() => deleteEdge(selEdge.id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors flex-shrink-0">
                            <Trash2 size={14} />
                        </button>
                    </div>
                );
            })()}

            {selectedNode && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/95 border border-slate-700 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl backdrop-blur-sm">
                    <Edit2 size={13} className="text-slate-400 flex-shrink-0" />
                    <input autoFocus
                        className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white w-48 focus:outline-none focus:border-violet-400"
                        value={editLabel} onChange={e => setEditLabel(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && saveLabel()} />
                    <button onClick={saveLabel} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-lg transition-colors">Save</button>
                    <button onClick={() => setSelectedNode(null)} className="p-1.5 text-slate-500 hover:text-white"><X size={14} /></button>
                </div>
            )}

            <p className="absolute bottom-3 right-3 text-[9px] text-slate-700 font-mono pointer-events-none">
                scroll to zoom · drag bg to pan · hover node to trace · click edge to delete
            </p>
        </div>
    );
}

// ─── Sandbox canvas ───────────────────────────────────────────────────────────

const SB_COLORS = ["#7c3aed","#0ea5e9","#10b981","#f59e0b","#ef4444","#ec4899","#64748b"];

function SandboxCanvas() {
    const svgRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan]   = useState({ x: 40, y: 40 });
    const [panning, setPanning]   = useState(null);
    const [dragging, setDragging] = useState(null);

    const [nodes, setNodes] = useState(() => loadSandbox().nodes);
    const [edges, setEdges] = useState(() => loadSandbox().edges);

    const [connectMode, setConnectMode]     = useState(false);
    const [connectSource, setConnectSource] = useState(null);
    const [selectedNode, setSelectedNode]   = useState(null);
    const [selectedEdge, setSelectedEdge]   = useState(null);
    const [editLabel, setEditLabel]         = useState("");
    const [editEdgeId, setEditEdgeId]       = useState(null);
    const [savedFlash, setSavedFlash]       = useState(false);
    const nodeCounter = useRef(nodes.length);
    const saveTimer   = useRef(null);
    const flashTimer  = useRef(null);

    useEffect(() => {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            saveSandbox({ nodes, edges });
            setSavedFlash(true);
            clearTimeout(flashTimer.current);
            flashTimer.current = setTimeout(() => setSavedFlash(false), 1100);
        }, 400);
        return () => clearTimeout(saveTimer.current);
    }, [nodes, edges]);

    const onWheel = useCallback((e) => {
        e.preventDefault();
        const f  = e.deltaY < 0 ? 1.13 : 0.89;
        const r  = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - r.left, my = e.clientY - r.top;
        setZoom(z => {
            const nz = Math.min(4, Math.max(0.2, z * f));
            setPan(p => ({ x: mx-(mx-p.x)*(nz/z), y: my-(my-p.y)*(nz/z) }));
            return nz;
        });
    }, []);

    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [onWheel]);

    const addNode = () => {
        if (!svgRef.current) return;
        const r  = svgRef.current.getBoundingClientRect();
        const x  = (r.width  / 2 - pan.x) / zoom - NW / 2;
        const y  = (r.height / 2 - pan.y) / zoom - NH / 2;
        nodeCounter.current += 1;
        const color = SB_COLORS[nodeCounter.current % SB_COLORS.length];
        setNodes(prev => [...prev, {
            id: `sb_${Date.now()}`,
            label: `Box ${nodeCounter.current}`,
            x: Math.max(0, x),
            y: Math.max(0, y),
            color,
        }]);
    };

    const toSvg = useCallback((cx, cy) => {
        const r = svgRef.current.getBoundingClientRect();
        return { x: (cx - r.left - pan.x) / zoom, y: (cy - r.top - pan.y) / zoom };
    }, [pan, zoom]);

    const onSvgMouseDown = useCallback((e) => {
        if (!dragging) { e.preventDefault(); setPanning({ sx: e.clientX - pan.x, sy: e.clientY - pan.y }); }
    }, [pan, dragging]);

    const onNodeMouseDown = useCallback((e, id) => {
        e.stopPropagation();
        if (connectMode) return;
        e.preventDefault();
        const { x, y } = toSvg(e.clientX, e.clientY);
        const n = nodes.find(nd => nd.id === id);
        if (!n) return;
        setDragging({ id, ox: x - n.x, oy: y - n.y });
        setSelectedEdge(null);
    }, [nodes, connectMode, toSvg]);

    const onNodeClick = useCallback((e, id) => {
        e.stopPropagation();
        if (connectMode) {
            if (!connectSource) {
                setConnectSource(id);
            } else if (connectSource !== id) {
                setEdges(prev => [...prev, { id: `se_${Date.now()}`, from: connectSource, to: id, label: "" }]);
                setConnectSource(null);
            } else {
                setConnectSource(null);
            }
            return;
        }
        const n = nodes.find(nd => nd.id === id);
        setSelectedNode(id);
        setEditLabel(n?.label || "");
        setSelectedEdge(null);
        setEditEdgeId(null);
    }, [connectMode, connectSource, nodes]);

    const onMouseMove = useCallback((e) => {
        if (dragging) {
            const { x, y } = toSvg(e.clientX, e.clientY);
            setNodes(prev => prev.map(n =>
                n.id === dragging.id ? { ...n, x: Math.max(0, x-dragging.ox), y: Math.max(0, y-dragging.oy) } : n
            ));
        } else if (panning) {
            setPan({ x: e.clientX - panning.sx, y: e.clientY - panning.sy });
        }
    }, [dragging, panning, toSvg]);

    const onMouseUp = useCallback(() => { setDragging(null); setPanning(null); }, []);

    const saveNodeLabel = () => {
        if (editLabel.trim()) setNodes(prev => prev.map(n => n.id === selectedNode ? { ...n, label: editLabel } : n));
        setSelectedNode(null);
    };

    const deleteNode = (id) => {
        setNodes(prev => prev.filter(n => n.id !== id));
        setEdges(prev => prev.filter(e => e.from !== id && e.to !== id));
        setSelectedNode(null);
    };

    const clearAll = () => {
        if (!window.confirm("Clear all sandbox boxes and connections?")) return;
        setNodes([]); setEdges([]);
        nodeCounter.current = 0;
        setConnectSource(null); setSelectedNode(null); setSelectedEdge(null);
    };

    const byId = {};
    nodes.forEach(n => (byId[n.id] = n));
    const selEdgeObj = edges.find(e => e.id === selectedEdge);

    return (
        <div className="relative flex-1 overflow-hidden bg-[#07101e] rounded-xl select-none">

            {/* Toolbar */}
            <div className="absolute top-3 left-3 z-20 flex items-center gap-2 flex-wrap">
                <button onClick={addNode}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-lg transition-colors">
                    <Plus size={12} /> Add Box
                </button>
                <button onClick={() => { setConnectMode(m => !m); setConnectSource(null); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                        connectMode
                            ? "bg-sky-500/20 text-sky-300 border-sky-400/40"
                            : "bg-slate-800 text-slate-400 hover:text-white border-slate-700"
                    }`}>
                    <Link2 size={12} />
                    {connectMode ? (connectSource ? "Click target…" : "Click source…") : "Connect"}
                </button>
                {connectMode && (
                    <button onClick={() => { setConnectMode(false); setConnectSource(null); }}
                        className="px-2 py-1.5 text-slate-500 hover:text-white text-xs rounded-lg border border-slate-700 bg-slate-800 transition-colors">
                        Cancel
                    </button>
                )}
                <button onClick={clearAll}
                    className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-lg border border-red-500/20 transition-colors">
                    Clear All
                </button>
                {savedFlash && <span className="text-[9px] text-emerald-400 font-bold font-mono animate-pulse">saved</span>}
            </div>

            {/* Zoom controls */}
            <div className="absolute top-3 right-3 z-20 flex flex-col items-center gap-1">
                <button onClick={() => setZoom(z => Math.min(4, z * 1.2))}
                    className="w-7 h-7 bg-slate-800/90 hover:bg-slate-700 text-slate-300 rounded-lg flex items-center justify-center text-sm font-bold border border-slate-700">+</button>
                <span className="text-[9px] text-slate-500 font-mono py-0.5">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(z => Math.max(0.2, z / 1.2))}
                    className="w-7 h-7 bg-slate-800/90 hover:bg-slate-700 text-slate-300 rounded-lg flex items-center justify-center text-sm font-bold border border-slate-700">−</button>
                <button onClick={() => { setZoom(1); setPan({ x: 40, y: 40 }); }}
                    className="w-7 h-7 bg-slate-800/90 hover:bg-slate-700 text-slate-400 rounded-lg flex items-center justify-center border border-slate-700 mt-0.5">
                    <Maximize2 size={11} />
                </button>
            </div>

            {/* Connect-mode source hint */}
            {connectMode && connectSource && (
                <div className="absolute top-14 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                    <span className="bg-sky-500/20 border border-sky-500/40 text-sky-300 text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                        Source selected — click a target box to draw the connection
                    </span>
                </div>
            )}

            {/* Empty state */}
            {nodes.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <p className="text-5xl mb-3 text-slate-800">⬜</p>
                    <p className="text-sm font-bold text-slate-600">Free canvas — add boxes and connect them</p>
                    <p className="text-[10px] text-slate-700 mt-1.5">Won't affect the live Diagram · auto-saved to your browser</p>
                </div>
            )}

            {/* SVG */}
            <svg ref={svgRef} className="w-full h-full"
                onMouseDown={onSvgMouseDown} onMouseMove={onMouseMove}
                onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
                onClick={() => { setSelectedEdge(null); setEditEdgeId(null); if (!connectMode) setSelectedNode(null); }}
                style={{ cursor: dragging ? "grabbing" : panning ? "grabbing" : connectMode ? "crosshair" : "default" }}>

                <defs>
                    <marker id="sb-arr" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
                        <path d="M0,0.5 L0,5.5 L7,3 z" fill="#64748b" />
                    </marker>
                    <pattern id="sb-grid" width={20*zoom} height={20*zoom} patternUnits="userSpaceOnUse"
                        x={pan.x % (20*zoom)} y={pan.y % (20*zoom)}>
                        <circle cx={0} cy={0} r={0.8} fill="#1a2744" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#sb-grid)" />

                <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
                    {/* Edges */}
                    {edges.map((edge, idx) => {
                        const from = byId[edge.from], to = byId[edge.to];
                        if (!from || !to) return null;
                        const fx = from.x+NW/2, fy = from.y+NH/2;
                        const tx = to.x+NW/2,   ty = to.y+NH/2;
                        const mx = (fx+tx)/2, my = (fy+ty)/2;
                        const dx = tx-fx, dy = ty-fy;
                        const l  = Math.sqrt(dx*dx+dy*dy) || 1;
                        const pull = 28 + (idx % 5) * 8;
                        const cpx = mx-(dy/l)*pull, cpy = my+(dx/l)*pull;
                        const d   = `M ${fx} ${fy} Q ${cpx} ${cpy} ${tx} ${ty}`;
                        const midX = fx/4+cpx/2+tx/4, midY = fy/4+cpy/2+ty/4;
                        const isSel = selectedEdge === edge.id;
                        return (
                            <g key={edge.id}>
                                <path d={d} fill="none" stroke="transparent" strokeWidth={14} style={{ cursor: "pointer" }}
                                    onClick={e => { e.stopPropagation(); setSelectedEdge(edge.id); setSelectedNode(null); setEditEdgeId(null); }} />
                                <path d={d} fill="none"
                                    stroke={isSel ? "#e2e8f0" : "#334155"} strokeWidth={isSel ? 2.2 : 1.5}
                                    markerEnd="url(#sb-arr)" style={{ cursor: "pointer" }}
                                    onClick={e => { e.stopPropagation(); setSelectedEdge(edge.id); setSelectedNode(null); setEditEdgeId(null); }} />
                                {edge.label && (
                                    <text x={midX} y={midY-7} textAnchor="middle" fill="#64748b" fontSize={9}
                                        style={{ pointerEvents: "none" }}>{edge.label}</text>
                                )}
                            </g>
                        );
                    })}

                    {/* Nodes */}
                    {nodes.map(node => {
                        const isSel = selectedNode === node.id;
                        const isSrc = connectSource === node.id;
                        const color = node.color || "#94a3b8";
                        return (
                            <g key={node.id} transform={`translate(${node.x},${node.y})`}
                                style={{ cursor: connectMode ? "pointer" : "grab" }}
                                onMouseDown={e => onNodeMouseDown(e, node.id)}
                                onClick={e => onNodeClick(e, node.id)}>
                                <rect width={NW} height={NH} rx={8}
                                    fill={isSrc ? color+"50" : isSel ? color+"30" : color+"1a"}
                                    stroke={color} strokeWidth={isSrc ? 3 : isSel ? 2.5 : 1.5}
                                    strokeDasharray={isSrc ? "5 2.5" : undefined}
                                    filter={isSrc ? `drop-shadow(0 0 8px ${color})` : isSel ? `drop-shadow(0 0 6px ${color}80)` : undefined} />
                                <text x={NW/2} y={NH/2+1} textAnchor="middle" dominantBaseline="middle"
                                    fontSize={10} fontWeight="700" fill={color}
                                    style={{ pointerEvents: "none", userSelect: "none" }}>
                                    {node.label}
                                </text>
                            </g>
                        );
                    })}
                </g>
            </svg>

            {/* Edge actions panel */}
            {selEdgeObj && !selectedNode && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/95 border border-slate-700 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl backdrop-blur-sm">
                    {editEdgeId === selEdgeObj.id ? (
                        <>
                            <input autoFocus
                                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white w-52 focus:outline-none focus:border-violet-400"
                                value={editLabel} onChange={e => setEditLabel(e.target.value)}
                                placeholder="Label for this connection…"
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        setEdges(prev => prev.map(ed => ed.id === selEdgeObj.id ? { ...ed, label: editLabel } : ed));
                                        setEditEdgeId(null);
                                    }
                                    if (e.key === "Escape") setEditEdgeId(null);
                                }} />
                            <button onClick={() => {
                                setEdges(prev => prev.map(ed => ed.id === selEdgeObj.id ? { ...ed, label: editLabel } : ed));
                                setEditEdgeId(null);
                            }} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-lg">Save</button>
                        </>
                    ) : (
                        <button onClick={() => { setEditLabel(selEdgeObj.label || ""); setEditEdgeId(selEdgeObj.id); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg border border-slate-700 transition-colors">
                            <Edit2 size={11} /> {selEdgeObj.label || "Add label…"}
                        </button>
                    )}
                    <button onClick={() => { setEdges(prev => prev.filter(e => e.id !== selEdgeObj.id)); setSelectedEdge(null); }}
                        className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors">
                        <Trash2 size={14} />
                    </button>
                </div>
            )}

            {/* Node actions panel */}
            {selectedNode && !connectMode && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/95 border border-slate-700 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl backdrop-blur-sm">
                    <Edit2 size={13} className="text-slate-400 flex-shrink-0" />
                    <input autoFocus
                        className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white w-40 focus:outline-none focus:border-violet-400"
                        value={editLabel} onChange={e => setEditLabel(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && saveNodeLabel()} />
                    <div className="flex gap-1 flex-shrink-0">
                        {SB_COLORS.map(c => (
                            <button key={c}
                                onClick={() => setNodes(prev => prev.map(n => n.id === selectedNode ? { ...n, color: c } : n))}
                                className="w-4 h-4 rounded-full border-2 border-transparent hover:border-white transition-colors flex-shrink-0"
                                style={{ background: c }} />
                        ))}
                    </div>
                    <button onClick={saveNodeLabel} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-lg transition-colors">Save</button>
                    <button onClick={() => deleteNode(selectedNode)}
                        className="p-1.5 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors">
                        <Trash2 size={13} />
                    </button>
                    <button onClick={() => setSelectedNode(null)} className="p-1.5 text-slate-500 hover:text-white transition-colors">
                        <X size={14} />
                    </button>
                </div>
            )}

            <p className="absolute bottom-3 right-3 text-[9px] text-slate-700 font-mono pointer-events-none">
                sandbox · isolated from live diagram · auto-saved to browser
            </p>
        </div>
    );
}

// ─── Relations table ──────────────────────────────────────────────────────────

function RelationsTable({ nodes, edges }) {
    const nodeMap = {};
    nodes.forEach(n => (nodeMap[n.id] = n));

    function NodeBadge({ id }) {
        const n = nodeMap[id] || DEFAULT_NODES.find(x => x.id === id);
        if (!n) return <span className="text-slate-500 text-xs font-mono">{id}</span>;
        const cat    = CATEGORIES[n.category] || CATEGORIES.infra;
        const isRole = n.category === "role";
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold border"
                style={{ color: cat.color, borderColor: cat.color+"50", background: cat.color+"15",
                    borderRadius: isRole ? "999px" : "6px", borderStyle: isRole ? "dashed" : "solid" }}>
                {isRole && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cat.color }} />}
                {n.label}
            </span>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto bg-[#07101e] rounded-xl">
            <div className="flex flex-wrap gap-3 px-6 py-3 border-b border-slate-800 sticky top-0 bg-[#07101e] z-10">
                {Object.entries(EDGE_TYPES).map(([k, et]) => (
                    <span key={k} className="flex items-center gap-1.5 text-[9px] font-bold" style={{ color: et.color }}>
                        <span className="w-2 h-2 rounded-full" style={{ background: et.color }} />{et.label}
                    </span>
                ))}
            </div>
            <table className="w-full text-left">
                <thead>
                    <tr className="text-[9px] font-bold text-slate-600 uppercase tracking-widest border-b border-slate-800">
                        <th className="px-6 py-3">From</th>
                        <th className="px-6 py-3">To</th>
                        <th className="px-6 py-3">Action</th>
                        <th className="px-6 py-3">Type</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                    {edges.map(edge => {
                        const et = EDGE_TYPES[edge.type] || EDGE_TYPES.request;
                        return (
                            <tr key={edge.id} className="hover:bg-slate-900/60 transition-colors">
                                <td className="px-6 py-3"><NodeBadge id={edge.from} /></td>
                                <td className="px-6 py-3"><NodeBadge id={edge.to} /></td>
                                <td className="px-6 py-3 text-sm text-slate-300">{edge.label}</td>
                                <td className="px-6 py-3">
                                    <span className="text-[10px] font-bold" style={{ color: et.color }}>{et.label}</span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Add edge form ────────────────────────────────────────────────────────────

function AddEdgePanel({ nodes, onAdd, onClose }) {
    const [f, setF] = useState({ from: "", to: "", type: "request", label: "" });
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    const submit = () => {
        if (f.from && f.to && f.label.trim()) { onAdd({ id: `e${Date.now()}`, ...f }); onClose(); }
    };
    return (
        <div className="bg-slate-900 border-t border-slate-700 px-6 py-4 flex-shrink-0">
            <div className="flex items-end gap-3 flex-wrap">
                {["from", "to"].map(k => (
                    <div key={k}>
                        <p className="text-[9px] text-slate-500 uppercase mb-1">{k}</p>
                        <select className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-400 min-w-[160px]"
                            value={f[k]} onChange={e => set(k, e.target.value)}>
                            <option value="">Select component…</option>
                            {nodes.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
                        </select>
                    </div>
                ))}
                <div>
                    <p className="text-[9px] text-slate-500 uppercase mb-1">Type</p>
                    <select className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-400"
                        value={f.type} onChange={e => set("type", e.target.value)}>
                        {Object.entries(EDGE_TYPES).map(([k, et]) => <option key={k} value={k}>{et.label}</option>)}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <p className="text-[9px] text-slate-500 uppercase mb-1">Action description</p>
                    <input className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-400"
                        placeholder="e.g. Pushes order confirmation"
                        value={f.label} onChange={e => set("label", e.target.value)}
                        onKeyDown={e => e.key === "Enter" && submit()} />
                </div>
                <button onClick={submit} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold rounded-lg transition-colors flex-shrink-0">Add</button>
                <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-white transition-colors flex-shrink-0"><X size={16} /></button>
            </div>
        </div>
    );
}

// ─── Registry tab (nav scanner) ──────────────────────────────────────────────

function RegistryTab({ nodes, onAddToMap }) {
    const [filter, setFilter]     = useState("unregistered");
    const [pickedCat, setPickedCat] = useState({}); // label → category
    const [dismissed, setDismissed] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem("tg_scanner_dismissed") || "[]")); }
        catch { return new Set(); }
    });

    const allItems = useMemo(() => scanNav(nodes), [nodes]);

    const saveDismissed = (set) => {
        localStorage.setItem("tg_scanner_dismissed", JSON.stringify([...set]));
        setDismissed(set);
    };
    const dismiss   = (label) => saveDismissed(new Set([...dismissed, label]));
    const undismiss = (label) => { const s = new Set(dismissed); s.delete(label); saveDismissed(s); };

    const addToMap = (item) => {
        const cat  = pickedCat[item.label] || "orchestration";
        const maxY = nodes.length ? Math.max(...nodes.map(n => n.y)) : 500;
        onAddToMap({
            id:       `nav_${item.label.toLowerCase().replace(/[^a-z0-9]/g, "_")}_${Date.now()}`,
            label:    item.label,
            category: cat,
            x:        1020,
            y:        maxY + 80,
        });
        dismiss(item.label); // auto-dismiss after adding (won't show as "unregistered" anymore)
    };

    const needsReg  = allItems.filter(i => !i.isUIView && !i.isMatched && !dismissed.has(i.label));
    const registered = allItems.filter(i => !i.isUIView && i.isMatched);
    const uiViews   = allItems.filter(i => i.isUIView);
    const allVisible = allItems.filter(i => !dismissed.has(i.label) || i.isMatched);

    const shown = filter === "unregistered" ? needsReg
                : filter === "matched"      ? registered
                : filter === "uiview"       ? uiViews
                : allVisible;

    const FILTERS = [
        { key: "unregistered", label: `Needs registration (${needsReg.length})`, cls: "text-orange-400" },
        { key: "matched",      label: `Registered (${registered.length})`,       cls: "text-emerald-400" },
        { key: "uiview",       label: `UI views (${uiViews.length})`,            cls: "text-slate-500"  },
        { key: "all",          label: "All",                                      cls: "text-slate-400"  },
    ];

    return (
        <div className="flex-1 flex flex-col overflow-hidden bg-[#07101e] rounded-xl">
            {/* Sticky toolbar */}
            <div className="sticky top-0 bg-[#07101e] z-10 px-5 py-3 border-b border-slate-800 flex items-center gap-2 flex-wrap">
                {FILTERS.map(f => (
                    <button key={f.key} onClick={() => setFilter(f.key)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors border ${
                            filter === f.key ? `bg-slate-800 border-slate-600 ${f.cls}` : "border-slate-800/0 text-slate-600 hover:text-slate-400"
                        }`}>
                        {f.label}
                    </button>
                ))}
                <span className="ml-auto text-[9px] text-slate-700 font-mono hidden md:inline">
                    auto-scanned from DashboardLayout.js → navItems
                </span>
            </div>

            {/* Empty state */}
            {shown.length === 0 && (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-16 pointer-events-none">
                    <p className="text-3xl mb-2 text-slateite-700">✓</p>
                    <p className="text-sm font-bold text-slate-500">All platform nav items are registered</p>
                    <p className="text-[10px] text-slate-700 mt-1">Add a new item to navItems in DashboardLayout.js to see it appear here</p>
                </div>
            )}

            {/* Item list */}
            <div className="overflow-y-auto flex-1 divide-y divide-slate-800/40">
                {shown.map(item => {
                    const isDismissed = dismissed.has(item.label) && !item.isMatched;
                    return (
                        <div key={item.label}
                            className={`px-5 py-3 flex items-center gap-3 hover:bg-slate-900/40 transition-colors ${isDismissed ? "opacity-40" : ""}`}>

                            {/* Status dot */}
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-0.5 ${
                                item.isUIView  ? "bg-slate-700" :
                                item.isMatched ? "bg-emerald-500" :
                                isDismissed    ? "bg-slate-600" :
                                "bg-orange-500 animate-pulse shadow-[0_0_5px_#f97316]"
                            }`} />

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className={`text-sm font-semibold ${
                                        item.isUIView || isDismissed ? "text-slate-600" : "text-white"
                                    }`}>{item.label}</span>
                                    {item.roles.map(role => {
                                        const rc = ROLE_COLORS[role] || { bg: "#475569" };
                                        return (
                                            <span key={role} className="text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                                                style={{ background: rc.bg + "28", color: rc.bg, border: `1px solid ${rc.bg}35` }}>
                                                {role}
                                            </span>
                                        );
                                    })}
                                </div>
                                <p className="text-[9px] text-slate-700 font-mono mt-0.5">{item.path}</p>
                            </div>

                            {/* Right side actions */}
                            {item.isUIView ? (
                                <span className="text-[9px] text-slate-700 italic flex-shrink-0">UI view — excluded</span>
                            ) : item.isMatched ? (
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {item.matchedNode && (() => {
                                        const cat = CATEGORIES[item.matchedNode.category] || CATEGORIES.infra;
                                        return (
                                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-md border"
                                                style={{ color: cat.color, borderColor: cat.color+"50", background: cat.color+"15" }}>
                                                {item.matchedNode.label}
                                            </span>
                                        );
                                    })()}
                                    <span className="text-[9px] font-bold text-emerald-600">✓ registered</span>
                                </div>
                            ) : isDismissed ? (
                                <button onClick={() => undismiss(item.label)}
                                    className="text-[9px] text-slate-600 hover:text-orange-400 underline flex-shrink-0 transition-colors">
                                    Restore
                                </button>
                            ) : (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <select
                                        className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-[10px] text-white focus:outline-none focus:border-orange-400"
                                        value={pickedCat[item.label] || "orchestration"}
                                        onChange={e => setPickedCat(p => ({ ...p, [item.label]: e.target.value }))}>
                                        {Object.entries(CATEGORIES).filter(([k]) => k !== "role").map(([k, cat]) => (
                                            <option key={k} value={k}>{cat.label}</option>
                                        ))}
                                    </select>
                                    <button onClick={() => addToMap(item)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-500/20 text-orange-300 text-[10px] font-bold rounded-lg border border-orange-500/30 hover:bg-orange-500/30 transition-colors">
                                        <Plus size={10} /> Add to Map
                                    </button>
                                    <button onClick={() => dismiss(item.label)} title="Intentionally excluded — hide from list"
                                        className="p-1 text-slate-600 hover:text-slate-400 transition-colors flex-shrink-0">
                                        <X size={11} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="px-5 py-2.5 border-t border-slate-800 flex items-center gap-2">
                <p className="text-[9px] text-slate-700">
                    To permanently register a component: add it to <span className="font-mono text-slate-600">relationMap.config.js → DEFAULT_NODES</span>
                </p>
            </div>
        </div>
    );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function RelationMapModal({ onClose, initialNodes = DEFAULT_NODES, initialEdges = DEFAULT_EDGES }) {
    const [nodes, setNodes] = useState(() => {
        const { nodes: n } = loadLayout(initialNodes, initialEdges);
        return n;
    });
    const [edges, setEdges] = useState(() => {
        const { edges: e } = loadLayout(initialNodes, initialEdges);
        return e;
    });
    const [tab, setTab]         = useState("diagram");
    const [adding, setAdding]   = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const saveTimer  = useRef(null);
    const flashTimer = useRef(null);

    useEffect(() => {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            savePositions(nodes);
            setSavedFlash(true);
            clearTimeout(flashTimer.current);
            flashTimer.current = setTimeout(() => setSavedFlash(false), 1200);
        }, 350);
        return () => clearTimeout(saveTimer.current);
    }, [nodes]);

    useEffect(() => { saveEdges(edges); }, [edges]);

    const handleResetLayout = () => {
        localStorage.removeItem(LS_NODES);
        localStorage.removeItem(LS_EDGES);
        setNodes(initialNodes.map(n => ({ ...n })));
        setEdges(initialEdges.map(e => ({ ...e })));
    };

    const handleAlign = () => { setNodes(prev => autoAlign(prev)); };

    const handleAddToMap = (node) => { setNodes(prev => [...prev, node]); };

    const unregisteredCount = useMemo(() => countUnregistered(nodes), [nodes]);

    const TABS = [
        { key: "diagram",  icon: <GitBranch size={11} />, label: "Diagram"   },
        { key: "table",    icon: <Table2    size={11} />, label: "Relations"  },
        { key: "sandbox",  icon: <Layers    size={11} />, label: "Sandbox"    },
        { key: "registry", icon: <Search    size={11} />, label: "Registry",  badge: unregisteredCount },
    ];

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm p-3">
            <div className="flex-1 flex flex-col bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700/80 min-h-0">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 flex-shrink-0">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-base font-bold text-white">Platform Relation Map</h2>
                            <span className="text-[9px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full font-mono">
                                {nodes.length} components · {edges.length} relations
                            </span>
                            <span className="hidden sm:flex items-center gap-1 text-[9px] font-bold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">
                                <Lock size={8} /> All platform components must be registered here first
                            </span>
                            {unregisteredCount > 0 && (
                                <button onClick={() => setTab("registry")}
                                    className="flex items-center gap-1 text-[9px] font-bold text-orange-300 bg-orange-500/15 border border-orange-500/30 px-2 py-0.5 rounded-full hover:bg-orange-500/25 transition-colors">
                                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                                    {unregisteredCount} unregistered nav items
                                </button>
                            )}
                            {tab === "sandbox" && (
                                <span className="flex items-center gap-1 text-[9px] font-bold text-violet-400 bg-violet-400/10 border border-violet-400/20 px-2 py-0.5 rounded-full">
                                    <Layers size={8} /> Free canvas — isolated from live diagram
                                </span>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-600 mt-0.5">
                            {tab === "sandbox"  ? "Add boxes · draw connections · rename & colour · auto-saved"
                           : tab === "registry" ? "Scanned from DashboardLayout.js navItems — orange items need to be added to the map"
                           : "Hover a node to trace connections · scroll to zoom · drag background to pan"}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        {/* Category legend — only on diagram */}
                        {tab === "diagram" && (
                            <div className="hidden xl:flex flex-wrap gap-x-2 gap-y-1 max-w-xs">
                                {Object.entries(CATEGORIES).map(([key, cat]) => (
                                    <span key={key} className="flex items-center gap-1 text-[8px] font-bold" style={{ color: cat.color }}>
                                        {key === "role"
                                            ? <span className="w-3 h-1.5 rounded-full border flex-shrink-0" style={{ borderColor: cat.color, borderStyle: "dashed" }} />
                                            : <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: cat.color }} />
                                        }
                                        {cat.label}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="flex bg-slate-800 p-0.5 rounded-lg gap-0.5">
                            {TABS.map(({ key, icon, label, badge }) => (
                                <button key={key} onClick={() => setTab(key)}
                                    className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                                        tab === key
                                            ? key === "sandbox"  ? "bg-violet-700/80 text-violet-100"
                                            : key === "registry" ? "bg-orange-900/60 text-orange-200"
                                            : "bg-slate-600 text-white"
                                            : "text-slate-400 hover:text-white"
                                    }`}>
                                    {icon}{label}
                                    {badge > 0 && (
                                        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-orange-500 text-white text-[8px] font-black rounded-full flex items-center justify-center leading-none">
                                            {badge > 9 ? "9+" : badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Add Relation (only on diagram/table) */}
                        {tab !== "sandbox" && tab !== "registry" && (
                            <button onClick={() => setAdding(a => !a)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                                    adding ? "bg-violet-800 text-violet-200" : "bg-violet-600 hover:bg-violet-500 text-white"
                                }`}>
                                <Plus size={13} /> Add Relation
                            </button>
                        )}

                        <button onClick={onClose} className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Canvas area */}
                <div className="flex-1 flex flex-col overflow-hidden p-3 min-h-0">
                    {tab === "diagram" && (
                        <Diagram nodes={nodes} edges={edges} setNodes={setNodes} setEdges={setEdges}
                            onAlign={handleAlign} onResetLayout={handleResetLayout} savedFlash={savedFlash} />
                    )}
                    {tab === "table"    && <RelationsTable nodes={nodes} edges={edges} />}
                    {tab === "sandbox"  && <SandboxCanvas />}
                    {tab === "registry" && <RegistryTab nodes={nodes} onAddToMap={handleAddToMap} />}
                </div>

                {/* Add relation form */}
                {adding && tab !== "sandbox" && tab !== "registry" && (
                    <AddEdgePanel nodes={nodes} onAdd={e => setEdges(prev => [...prev, e])} onClose={() => setAdding(false)} />
                )}
            </div>
        </div>
    );
}
